import { PropType, defineComponent } from 'vue';
import i18n from '@/setup/i18n';
import useSessionStore from '@/stores/session';


/**
 * Mixin can be included in components which need a custom theme style class.
 * Per default, it uses the global defined theme in the store/modules/session. It can be overwritten
 * with the prop "customTheme"
 *
 * @mixin
 */
export default defineComponent({
  name: 'themes-mixin',

  props: {
    /**
     * Custom theme to overwrite the global defined theme id
     */
    customTheme: {
      type: String as PropType<string | null>,
      default: null,
    },
  },

  computed: {
    /**
     * Returns a string with the active theme (global theme if nothing specific is defined).
     *
     * @returns active theme id for BEM class name
     */
    theme(): string {
      return this.customTheme ?? (useSessionStore()?.theme || 'default');
    },
  },

  methods: {
    /**
     * Checks if the given theme (or themes) matches the current theme.
     *
     * @param theme - A single theme name or a list of theme names.
     * @returns `true` if the theme matches, otherwise `false`.
     */
    isTheme(theme: string | string[]): boolean {
      if (Array.isArray(theme)) {
        return theme.includes(this.theme);
      }

      return this.theme === theme;
    },

    /**
     * Matches the translation key for the current theme.
     *
     * @param options - Object with the default translation key and the translation key for the current theme.
     * @param values - Object to be passed to i18n for string interpolation.
     *
     * @returns Translation string, or default key if no translation is found.
     */
    matchThemeTranslation(
      options: Record<string, string>,
      values: Record<string, never>
    ): string {
      let key = options.default;

      if (i18n.global.te(options[this.theme])) {
        key = options[this.theme];
      }

      return i18n.global.t(key, values) ?? key;
    },

    /**
     * Matches the value for the current theme from the given options.
     *
     * @param options - Object with the default value and the value for the current theme.
     *
     * @returns Value for the current theme, or default if no value is found.
     */
    matchTheme<T>(options: Record<string, T>): T | null {
      try {
        return this.theme in options ? options[this.theme] : options.default;
      } catch (error: never) {
        console.error('Key does not exist in theme config.', error.message); // eslint-disable-line no-console

        return null;
      }
    },
  },
});
